/*!

=========================================================
* Argon Dashboard PRO React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2020 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";

// nodejs library that concatenates classes
import classnames from "classnames";

// reactstrap components
import {
  Alert,
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Container,
  Row,
  Col,
} from "reactstrap";

// core components
import AuthHeader from "components/Headers/AuthHeader.js";

// Auth imports.
import { Auth } from "aws-amplify";
import { UserContext } from "../../../lib/UserContext";

class Login extends React.Component {
  static contextType = UserContext;
  state = {
    email: "",
    password: "",
    loginFailed: false,
    disableSubmitButton: false
  };

  // Handle onChange event for inputs.
  handleInputChange = (event) => {
    // Reset login error message.
    this.setState((s) => ({...s, loginFailed: false }));

    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    this.setState({
      [name]: value,
    });
  };

  // Handle form submission.
  handleSubmit = async (event) => {
    event.preventDefault();

    // Disable login button.
    this.context.setState(s => { return { ...s, loading: true }});

    try {
      this.state.remember
        ? Auth.configure({ storage: localStorage })
        : Auth.configure({ storage: sessionStorage });

      const user = await Auth.signIn(this.state.email, this.state.password);

      if (this.context.tenants && this.context.tenants.length >= 1 && !this.context.activeTenant) {
        const tenantId = localStorage.getItem("tenantId");
        if (!tenantId) {
          this.context.selectTenant(this.context.tenants[0]);
        }
      }

      if (this.context.events && this.context.events.length >= 1 && !this.context.activeEvent) {
        const eventId = localStorage.getItem("eventId");
        if (!eventId) {
          this.context.selectEvent(this.context.events[0]);
        }
      }

      // Update our user context provider.
      // Once this state change is picked
      // up by React, it will automatically
      // redirect user to dashboard.
      this.context.setState((state) => ({ ...state, user, loading: false }));
    } catch (error) {
      this.setState((s) => ({ ...s, loginFailed: true, email: "", password: "" }));
      this.context.setState(s => { return { ...s, loading: false }});
    }
  };

  render() {
    return (
      <>
        <AuthHeader />
        <Container className="mt--9 pb-5">
          <Row className="justify-content-center">
            <Col lg="5" md="7">
              <div className="text-center text-white text-muted mb-4">
                <span>Sign In</span>
              </div>
              <Card className="bg-secondary border-0 mb-0" style={{borderRadius: 20}}>
                <CardBody className="px-lg-5 py-lg-5">
                  {this.state.loginFailed && (
                    <Alert color="danger">
                      Your account information was entered incorrectly.
                    </Alert>
                  )}

                  <Form role="form" onSubmit={this.handleSubmit}>
                    <FormGroup
                      className={classnames("mb-3", {
                        focused: this.state.focusedEmail,
                      })}
                    >
                      <InputGroup className="input-group-merge input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-email-83" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          name="email"
                          placeholder="Email"
                          type="email"
                          required
                          value={this.state.email}
                          onChange={this.handleInputChange}
                          onFocus={() =>
                            this.setState((s) => ({ ...s, focusedEmail: true }))
                          }
                          onBlur={() =>
                            this.setState((s) => ({
                              ...s,
                              focusedEmail: false,
                            }))
                          }
                        />
                      </InputGroup>
                    </FormGroup>
                    <FormGroup
                      className={classnames({
                        focused: this.state.focusedPassword,
                      })}
                    >
                      <InputGroup className="input-group-merge input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-lock-circle-open" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          name="password"
                          placeholder="Password"
                          type="password"
                          required
                          value={this.state.password}
                          onChange={this.handleInputChange}
                          onFocus={() =>
                            this.setState({ focusedPassword: true })
                          }
                          onBlur={() =>
                            this.setState({ focusedPassword: false })
                          }
                        />
                      </InputGroup>
                    </FormGroup>
                    <div className="custom-control custom-control-alternative custom-checkbox">
                      <input
                        name="remember"
                        className="custom-control-input"
                        id=" customCheckLogin"
                        type="checkbox"
                        onChange={this.handleInputChange}
                      />
                      <label
                        className="custom-control-label"
                        htmlFor=" customCheckLogin"
                      >
                        <span className="text-muted">Remember me</span>
                      </label>
                    </div>
                    <div className="text-center">
                      <Button className="my-4" color="dark" type="submit" disabled={this.context.loading}>
                        Sign in
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
              <Row className="mt-3">
                <Col xs="6">
                  <a
                    className="text-dark"
                    href="/auth/forgot"
                  >
                    <small>Forgot password?</small>
                  </a>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </>
    );
  }
}

export default Login;
